import { ArrowRight, Loader2, Truck } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import ModalDropiIntegration from "../../../../components/integrations/Modals/ModalDropiIntegration";
import { useAuth } from "../../../../context/AuthContext";
import {
  getDropiIntegrations,
  postDropiIntegration,
} from "../../../../services/integrations/integrationsService";
import { dictionary, type Language } from "../../../../utils/dictionary";
import { FinanceOnboardingCta } from "./FinanceOnboardingCta";

interface StepProps {
  connected: boolean;
  onConnectionChange: (connected: boolean) => void;
  onNext: () => void;
}

const getDropiIntegrationErrorMessage = (error: unknown, language: Language) => {
  const data = (error as { response?: { data?: { trigger?: string; message?: string } } })?.response?.data;
  const trigger = data?.trigger;
  if (trigger && dictionary[trigger]?.[language]) return dictionary[trigger][language];
  return data?.message || dictionary["integration-error"][language];
};

export const Step3Dropi = ({ connected, onConnectionChange, onNext }: StepProps) => {
  const { language } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [modalOpenDropi, setModalOpenDropi] = useState(false);
  const [token, setToken] = useState("");

  const syncDropiIntegration = useCallback(async () => {
    try {
      const integration = await getDropiIntegrations();
      const active = integration?.is_active === true;
      onConnectionChange(active);
      return active;
    } catch {
      onConnectionChange(false);
      return false;
    }
  }, [onConnectionChange]);

  useEffect(() => {
    let mounted = true;

    const loadIntegration = async () => {
      setLoading(true);
      await syncDropiIntegration();
      if (mounted) setLoading(false);
    };

    loadIntegration();

    return () => {
      mounted = false;
    };
  }, [syncDropiIntegration]);

  const handlePostDropiIntegration = async (dropiToken: string) => {
    const cleanToken = dropiToken.trim();
    if (!cleanToken) return;

    setConnecting(true);
    try {
      await postDropiIntegration({
        integration_token: cleanToken,
        is_active: true,
      });
      onConnectionChange(true);
      setModalOpenDropi(false);
      setToken("");
      await syncDropiIntegration();
    } catch (error) {
      onConnectionChange(false);
      toast.error(getDropiIntegrationErrorMessage(error, language), { duration: 6000 });
    } finally {
      setConnecting(false);
    }
  };

  const isBusy = loading || connecting;

  return (
    <div className="w-full max-w-2xl bg-white shadow-xl rounded-[50px] p-8 md:p-12">
      <div className="mx-auto max-w-lg text-center">
        <h1 className="text-4xl font-bold mb-3">
          <span className="text-gray-900">Conecta </span>
          <span className="text-[#F38244]">Dropi</span>
        </h1>
        <p className="text-base font-medium text-[#64748B] leading-7">
          Sabrás cuánto dinero estás ganando de verdad.
        </p>
      </div>

      {connected ? (
        <>
          <div className="mx-auto mt-8 w-full max-w-md rounded-[24px] border border-gray-100 bg-white px-6 py-5 shadow-[0_8px_30px_rgba(67,104,229,0.08)]">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[16px] border border-orange-100 bg-orange-50 text-[#F38244]">
                <Truck className="h-7 w-7" />
              </div>

              <div className="min-w-0 flex-1">
                <h2 className="text-base font-bold text-gray-900">Dropi</h2>
                <p className="mt-1 text-sm font-medium text-[#64748B]">Logística y fulfillment</p>
              </div>

              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#a7f3d0] bg-[#ecfdf5] px-3 py-1 text-[11px] font-bold text-[#059669]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#10b981]" />
                Conectado
              </span>
            </div>
          </div>

          <div className="mt-8 flex justify-center">
            <FinanceOnboardingCta onClick={onNext}>
              Continuar <ArrowRight className="h-5 w-5" />
            </FinanceOnboardingCta>
          </div>
        </>
      ) : (
        <>
          <div className="mt-8 flex justify-center">
            <FinanceOnboardingCta onClick={() => setModalOpenDropi(true)} disabled={isBusy}>
              {isBusy ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Truck className="h-5 w-5" />
              )}
              Conectar Dropi
            </FinanceOnboardingCta>
          </div>

          <button
            type="button"
            onClick={onNext}
            className="mx-auto mt-5 inline-flex h-10 items-center justify-center rounded-lg px-2 text-sm font-semibold text-[#64748B] transition hover:bg-gray-50 hover:text-gray-900"
          >
            No uso Dropi, lo configuro manualmente
          </button>
        </>
      )}

      {modalOpenDropi ? (
        <ModalDropiIntegration
          setModalOpenDropi={setModalOpenDropi}
          handlePostDropiIntegration={handlePostDropiIntegration}
          token={token}
          setToken={setToken}
          language={language}
        />
      ) : null}
    </div>
  );
};
